import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Select, Tag, Space, Tabs, Input, Button } from 'antd';
import { UserOutlined, ReloadOutlined } from '@ant-design/icons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import thLocale from '@fullcalendar/core/locales/th'; 
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import CustomModal from './CustomModal';
import moment from 'moment-timezone';
import AllClassList from "./AllClassList";
import { confirmAlert } from 'react-confirm-alert'; // Import the confirmation dialog
import 'react-confirm-alert/src/react-confirm-alert.css'; // Import the styles
import 'antd/dist/reset.css';
import './Classes.css';
import ClassDetailsModal from './ClassDetailsModal';

const { Option } = Select;
const { TabPane } = Tabs;

function AllClasses() {
  const { t, i18n } = useTranslation();
  const [classes, setClasses] = useState([]);
   const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [teacherEvents, setTeacherEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [, setClassrooms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentEventInfo, setCurrentEventInfo] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [calendarView, setCalendarView] = useState('timeGridWeek');
  const [activeTab, setActiveTab] = useState('1');

const [calendarDate, setCalendarDate] = useState(new Date());
const calendarRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL;

  const parseScheduleHour = (scheduleHourString) => {
    const [hours, minutes] = scheduleHourString.split(' ').map((value, index) => (index === 0 ? parseInt(value) * 60 : parseInt(value)));
    return hours + minutes;
  };

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const teacher = teachers.find(t => t.userid === value);
    
    return (
      <Tag
        color={teacher?.color || '#108ee9'}
        closable={closable}
        onClose={onClose}
        style={{ 
          marginRight: 3,
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
        }}
      >
        <Space size={4}>
          <UserOutlined style={{ fontSize: '12px' }} />
          {label}
        </Space>
      </Tag>
    );
  };

const fetchClasses = useCallback(
  async (viewDate = new Date()) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No token found");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/classes/calendar-classes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          viewDate: viewDate.toISOString().split("T")[0], // YYYY-MM-DD
        },
      });


      // ✅ Use response.data.data
      setClasses(response.data?.data || []);
    } catch (error) {
      console.error("❌ Error fetching classes:", error);
      setError("Failed to fetch classes. Please ensure you are logged in.");
    } finally {
      setLoading(false);
    }
  },
  [API_URL]
);


 const fetchClassChangeLogs = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Properly call the setter and update the state
    setClassChangeLogs(response.data);

  } catch (error) {
    console.error('❌ Error fetching class change logs:', error);
  }
}, [API_URL]);
 
 const fetchTeacherEvents = useCallback(async (teacherIds) => {
    if (teacherIds.length === 0) {
      setTeacherEvents([]);
      return;
    }
  
    try {
      const token = localStorage.getItem('token');
      const promises = teacherIds.map((teacherId) =>
        axios.get(`${API_URL}/teachers/calendar/${teacherId}/events-with-shared`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(response => ({ 
          teacherId,  // Include teacherId with the response
          data: response.data 
        }))
      );
  
      const results = await Promise.all(promises);
      
      // Format events once when fetching
      const formattedEvents = results.flatMap((result) => 
        result.data.map(event => {
          const teacher = teachers.find(t => t.userid === result.teacherId); // Use the passed teacherId
          const isShared = event.is_shared || false;
  
          return {
            ...event,
            id: `event-${event.eventid}`,
            start: moment.utc(event.start).local().format(),
            end: moment.utc(event.end).local().format(),
            color: isShared ? '#005082' : (teacher ? teacher.color : '#444444'),
            backgroundColor: isShared 
              ? '#00508260' 
              : (teacher ? `${teacher.color}60` : '#44444480'),
            borderColor: isShared ? '#005082' : (teacher ? teacher.color : '#444444'),
            classNames: ['teacher-schedule-event']
          };
        })
      );
  
      setTeacherEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching teacher events:', error);
      setError('Failed to fetch events. Please try again later.');
    }
  }, [API_URL, teachers]);
  
  useEffect(() => {
    fetchClasses();
    fetchClassChangeLogs();
    setTeacherEvents([]); 
  }, [fetchClasses, fetchClassChangeLogs, fetchTeacherEvents]);
  

// fetchTeachers
const fetchTeachers = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/teachers`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const teachersWithColors = (response.data.teachers || []).map((teacher) => ({
      ...teacher,
      color: teacher.color || '#000000',
    }));

    setTeachers(teachersWithColors);
  } catch (error) {
    console.error('Error fetching teachers:', error);
  }
}, [API_URL]);

// fetchClassrooms
const fetchClassrooms = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/classrooms`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const classroomsData = response.data || [];

    // Filter classrooms with availability 'true'
    const availableClassrooms = classroomsData.filter(c => c.availability === true);

    // Sort by 'number' field
    const sortedClassrooms = availableClassrooms.sort((a, b) => {
      return a.number.localeCompare(b.number, undefined, { numeric: true, sensitivity: 'base' });
    });

    setClassrooms(sortedClassrooms);
  } catch (error) {
    console.error('Error fetching classrooms:', error);
  }
}, [API_URL]);

useEffect(() => {
  fetchTeachers();
}, [fetchTeachers]);

useEffect(() => {
  fetchClassrooms();
}, [fetchClassrooms]);

  

  useEffect(() => {
    if (selectedTeachers.length > 0) {
      fetchTeacherEvents(selectedTeachers);
    } else {
      setTeacherEvents([]);
    }
  }, [selectedTeachers, fetchTeacherEvents]);


const handleRefresh = () => {
  const calendarApi = calendarRef.current?.getApi();
  const currentDate = calendarApi?.getDate() || new Date();

  fetchClasses(currentDate);
  fetchClassChangeLogs();
  fetchTeachers();
  fetchClassrooms();

  if (selectedTeachers.length > 0) {
    fetchTeacherEvents(selectedTeachers);
  } else {
    setTeacherEvents([]);
  }
};


  const checkOverlap = (newStart, newEnd, teacherId, eventId, allClasses, teacherEvents) => {
    return [...allClasses, ...teacherEvents].some(event => {
      // Skip the current event being moved/resized
      if (event.classid?.toString() === eventId || event.eventid?.toString() === eventId) return false;
      
      // Check all events for the same teacher
      if (event.teacher_id !== teacherId && event.userid !== teacherId) return false;
  
      const eventStart = moment(event.start || `${event.date} ${event.schedule_time}`);
      const eventEnd = moment(event.end || `${event.date} ${event.end_time}`);
  
      return (
        newStart.isBetween(eventStart, eventEnd, null, '[)') ||
        newEnd.isBetween(eventStart, eventEnd, null, '(]') ||
        (newStart.isSameOrBefore(eventStart) && newEnd.isSameOrAfter(eventEnd))
      );
    });
  };

  const handleEventDrop = (info) => {
  const eventId = info.event.id;
  const classItem = classes.find(cls => cls.classid.toString() === eventId);

  // 🔒 Prevent moving if admin has confirmed
  if (classItem && classItem.adminapprove === 'adminconfirm') {
    confirmAlert({
      title: t('Action Not Allowed'),
      message: t('You cannot change an admin-confirmed class.'),
      buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
    });
    return;
  }

  const newStart = moment(info.event.start);
  const newEnd = moment(info.event.end);
  const teacherId = classItem?.teacher_id;

  const isOverlapping = checkOverlap(newStart, newEnd, teacherId, eventId, classes, teacherEvents);

  if (isOverlapping) {
    confirmAlert({
      title: t('Time Slot Conflict'),
      message: t('The selected time overlaps with an existing class or event for the same teacher. Please choose a different time.'),
      buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
    });
    return;
  }

  setCurrentEventInfo(info);
  setShowModal(true);
};

const handleEventResize = (info) => {
  const eventId = info.event.id;
  const classItem = classes.find(cls => cls.classid.toString() === eventId);

  // 🔒 Prevent resizing if admin has confirmed
  if (classItem && classItem.adminapprove === 'adminconfirm') {
    confirmAlert({
      title: t('Action Not Allowed'),
      message: t('You cannot change an admin-confirmed class.'),
      buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
    });
    return;
  }

  const newStart = moment(info.event.start);
  const newEnd = moment(info.event.end);
  const teacherId = classItem?.teacher_id;

  const isOverlapping = checkOverlap(newStart, newEnd, teacherId, eventId, classes, teacherEvents);

  if (isOverlapping) {
    confirmAlert({
      title: t('Time Slot Conflict'),
      message: t('The selected time overlaps with an existing class or event for the same teacher. Please choose a different time.'),
      buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
    });
    return;
  }

  setCurrentEventInfo(info);
  setShowModal(true);
};


const handleSubmitModal = async () => {
  const { event } = currentEventInfo;
  const classItem = classes.find(cls => cls.classid.toString() === event.id);

  const calendarApi = calendarRef.current?.getApi();
  const currentView = calendarApi?.view.type;
  const currentDate = calendarApi?.getDate();

  const originalStartTime = classItem.schedule_time; 
  const originalEndTime = classItem.end_time; 
  const newStartTime = event.start.toTimeString().split(' ')[0].substring(0, 5);
  const newEndTime = event.end.toTimeString().split(' ')[0].substring(0, 5);

  const originalDate = moment.tz(classItem.date, 'Asia/Bangkok').format('YYYY-MM-DD');
  const newDate = moment.tz(event.start, 'Asia/Bangkok').format('YYYY-MM-DD');

  const updatedEvent = {
    id: event.id,
    date: newDate,
    schedule_time: newStartTime,
    schedule_hour: ((event.end - event.start) / 60000).toFixed(0)
  };

  try {
    const token = localStorage.getItem('token');

    // Update the class
    await axios.put(`${API_URL}/classes/${updatedEvent.id}`, updatedEvent, {
      headers: { Authorization: `Bearer ${token}` }
    });

    // Log the change, but don’t let it crash if it fails
    try {
      await axios.post(`${API_URL}/class-change/class-change-logs`, {
        classid: updatedEvent.id,
        teacherid: classItem.teacher_id,
        originaldate: originalDate,
        originalstarttime: originalStartTime,
        originalendtime: originalEndTime,
        newdate: newDate,
        newstarttime: newStartTime,
        newendtime: newEndTime,
        changereason: changeReason,
        note: changeNote
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (logErr) {
      console.error("⚠️ Failed to log class change:", logErr);
    }

  } catch (error) {
    console.error('❌ Error updating event:', error);

  } finally {
    // Always close modal and reload data
    setShowModal(false);
    setCurrentEventInfo(null);
    setChangeReason('');
    setChangeNote('');

    await fetchClasses(currentDate);   // ✅ pass current calendar date
    await fetchClassChangeLogs();

    // Restore calendar view
    if (calendarApi && currentView && currentDate) {
      setTimeout(() => {
        calendarApi.changeView(currentView, currentDate);
      }, 100);
    }
  }
};


  // Reload the calendar after the details modal saves a change
  const refreshCalendarData = useCallback(async () => {
    const calendarApi = calendarRef.current?.getApi();
    const targetDate = calendarApi?.getDate() || new Date();
    await fetchClasses(targetDate);
    await fetchClassChangeLogs();
  }, [fetchClasses, fetchClassChangeLogs]);

  const handleEventClick = (info) => {
    const eventId = info.event.id;
    
    // For class events (not teacher events)
    if (!eventId.includes('event-')) {
      const classItem = classes.find(cls => cls.classid.toString() === eventId);
      if (classItem) {
        const teacher = teachers.find(t => t.userid === classItem.teacher_id);
        setSelectedClass({
          classData: classItem,
          teacherData: teacher
        });
        setDetailsModalVisible(true);
      }
    } else {
      // For teacher events - you can handle these differently or ignore them
      const eventIdNum = eventId.replace('event-', '');
      const teacherEvent = teacherEvents.find(evt => evt.eventid?.toString() === eventIdNum);
      if (teacherEvent) {

      }
    }
  };

  useEffect(() => {
  if (activeTab === '1') {
    // Calendar tab
    const calendarApi = calendarRef.current?.getApi();
    const currentDate = calendarApi?.getDate() || new Date();
    fetchClasses(currentDate);
    fetchClassChangeLogs();
    fetchTeachers();
    fetchClassrooms();
    if (selectedTeachers.length > 0) {
      fetchTeacherEvents(selectedTeachers);
    } else {
      setTeacherEvents([]);
    }
  }

}, [activeTab]);

  
const filteredClasses = classes
  .filter((classItem) => {
    const isTeacherMatch =
      selectedTeachers.length > 0
        ? selectedTeachers.includes(classItem.teacher_id)
        : true;
    
    const isSearchMatch = searchTerm === '' || 
      classItem.class_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classItem.subject_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return isTeacherMatch && isSearchMatch;
  });

const formatClassToEvent = (classItem) => {
  const startDate = new Date(classItem.date);
  const [startHours, startMinutes] = classItem.schedule_time.split(':').map(Number);

  if (isNaN(startHours) || isNaN(startMinutes)) {
      console.error('Invalid schedule time format:', classItem.schedule_time);
      return null;
  }

  startDate.setHours(startHours);
  startDate.setMinutes(startMinutes);
  startDate.setSeconds(0);
  startDate.setMilliseconds(0);

  if (isNaN(startDate.getTime())) {
      console.error('Invalid start date or time:', classItem.date, classItem.schedule_time, startDate);
      return null;
  }

  const totalMinutes = parseScheduleHour(classItem.schedule_hour || '');
  const endDateTime = new Date(startDate.getTime() + totalMinutes * 60000);

  if (isNaN(endDateTime.getTime())) {
      console.error('Invalid end date or time:', startDate, endDateTime, classItem.schedule_hour);
      return null;
  }

  const teacher = teachers.find((t) => t.userid === classItem.teacher_id);

  // Get platform icon and text
  const getPlatformIcon = (platform) => {
    switch(platform) {
      case 'online':
        return `💻 ${t('online')}`;
      case 'onsite':
        return `📍 ${t('onsite')}`;
      case 'athome':
        return `🏠 ${t('athome')}`;
      default:
        return `❓ ${platform}`;
    }
  };

  // Remove platform from nickname line
  const titleLines = [
    `${classItem.subject_name}`,
    `${classItem.nickname}`, // Platform removed from here
  ];

  return {
      id: classItem.classid.toString(),  
      title: titleLines.join('<br />'), 
      start: startDate.toISOString(), 
      end: endDateTime.toISOString(),
      color: teacher ? teacher.color : '#333333', 
      backgroundColor: teacher ? teacher.color : '#333333',
      // Add platform data to the event object
      extendedProps: {
        platform: getPlatformIcon(classItem.platform)
      }
  };
};

const renderEventContent = (arg) => {
  const event = arg.event;
  const startTime = event.start
    ? moment(event.start).format('HH:mm')
    : '';
  const endTime = event.end
    ? moment(event.end).format('HH:mm')
    : '';
  
  // Get platform from extended props
  const platform = event.extendedProps?.platform || '';
  
  return {
    html: `
      <div class="calendar-event teacher-event" style="
        padding: 1.5px;
        border-radius: 4px;
        min-height: 100%;
        font-size: 12px;
        position: relative;
      ">
        <div>${startTime} - ${endTime}</div>
        <div class="event-title" style="white-space:pre-line; margin-bottom: 20px;">${event.title}</div>
        <div class="platform-tag" style="
          position: absolute;
          bottom: 2px;
          left: 2px;
          margin-bottom: 3px;
          background: rgb(255, 255, 255);
          padding: 1px 4px;
          border-radius: 3px;
          font-size: 0.87em;
          color: #333;
        ">${platform}</div>
      </div>
    `,
  };
};

const calendarEvents = [
  ...filteredClasses.map(formatClassToEvent).filter((event) => event !== null),
  ...teacherEvents // Use directly since they're already formatted
];

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;



  return (
    <div className="class-container">
      <Sidebar />
      <Header />
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        size="large"
      >
      <TabPane tab="Calendar" key="1">
      <div className="top-filter-container">
      <div className="teacher-select-container">
        <div >
            <Input.Search
              placeholder={t('Search by Student Name (Course) or Subject')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '350px' }}
              allowClear
            />
          </div>
                <Select
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            placeholder={t('Select Teachers')}
            value={selectedTeachers}
            onChange={setSelectedTeachers}
            optionFilterProp="label" // Changed from children to label
            tagRender={tagRender}
            maxTagCount={3}
            maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length} ${t('more teachers')}`}
            filterOption={(input, option) => {
              // Check if the search term matches nickname or firstname
              return option?.label?.toLowerCase().includes(input.toLowerCase());
            }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          >
            {teachers.map(teacher => (
              <Option 
                key={teacher.userid} 
                value={teacher.userid}
                label={`${teacher.nickname}`} // Add label prop for search
                style={{
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Space>
                  <Tag color={teacher.color} style={{ margin: 0, width: 8, height: 8 }} />
                  <span>{teacher.nickname}</span>
                </Space>
              </Option>
            ))}
          </Select>
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
              >
                Refresh
              </Button>

      </div>
      </div>

    <div className="class-calendar-container">
      <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={calendarView}
          initialDate={calendarDate}
          allDaySlot={false}
          locale={i18n.language === 'th' ? thLocale : 'en'}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          slotDuration="00:15:00"
          scrollTime="09:00:00" 
          snapDuration="00:15:00" 
          slotLabelInterval="01:00" 
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false, 
            }}
          events={calendarEvents}
          editable={true}
          droppable={true}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventClick={handleEventClick}
          eventDisplay="block"
          eventBackgroundColor="transparent"
          eventContent={renderEventContent}
          eventClassNames={(info) => info.event.extendedProps.classNames}
          height="100%"
            datesSet={(dateInfo) => {
  const viewType = dateInfo.view.type;

  // Always use the focal date, not the first of the month
  const targetDate = dateInfo.view.calendar.getDate();

  const targetDateStr = targetDate.toLocaleDateString();
  const currentDateStr = calendarDate ? calendarDate.toLocaleDateString() : '';

  if (targetDateStr !== currentDateStr || calendarView !== viewType) {
    setCalendarDate(targetDate);
    setCalendarView(viewType);
    fetchClasses(targetDate);
  }
}}

          views={{
            dayGridMonth: {
              displayEventTime: true,
              displayEventEnd: true,
              dayMaxEvents: false
            },
            timeGridWeek: {
              displayEventTime: true,
              displayEventEnd: true
            },
            timeGridDay: {
              displayEventTime: true,
              displayEventEnd: true
            }
          }}
        />
    </div>
    <ClassDetailsModal
  visible={detailsModalVisible}
  onClose={() => setDetailsModalVisible(false)}
  classData={selectedClass?.classData}
  teacherData={selectedClass?.teacherData}
    classChangeLogs={classChangeLogs}
    onRefresh={refreshCalendarData}
/>
    
            </TabPane>
            <TabPane tab="All Class List" key="2">
            <AllClassList />
          </TabPane>
                    </Tabs>
            <CustomModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              onSubmit={handleSubmitModal}
              reason={changeReason}
              setReason={setChangeReason}
              note={changeNote}
              setNote={setChangeNote}
            />
          </div>
          
  );
  
}

export default withRoleAccess(AllClasses, ['admin', 'superadmin']);